/**
 * WelcomeDataPage - Step 2 of App Orientation Flow
 * 
 * Interactive tour of connected databases and tables.
 * Shows data quality scores, trends, and existing validations.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { useTablesData } from '../../hooks/useTablesData';
import { useValidationsSummary } from '../../hooks/useValidationsData';
import { userAPI } from '../../api/enhancedApiService';
import { 
  ServerIcon, 
  TableCellsIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import Logo from '../../components/common/Logo';

const WelcomeDataPage = () => {
  const navigate = useNavigate();
  const { connections } = useConnection();
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [realTableData, setRealTableData] = useState([]);

  // Use real data hooks
  const tablesQuery = useTablesData(selectedConnection?.id, {
    enabled: !!selectedConnection?.id
  });

  const validationsSummary = useValidationsSummary(selectedConnection?.id, {
    enabled: !!selectedConnection?.id
  });

  useEffect(() => {
    // Auto-select first connection when available
    if (connections && connections.length > 0 && !selectedConnection) {
      setSelectedConnection(connections[0]);
    }
  }, [connections, selectedConnection]);

  useEffect(() => {
    // Process real table data when available. useTablesData's `select`
    // returns `{tables, tierStatus, count}` (an object), not a bare array
    // — the previous Array.isArray guard always fell through.
    if (Array.isArray(tablesQuery.data?.tables)) {
      const processedTables = tablesQuery.data.tables.slice(0, 6).map((table, index) => ({
        name: table.table_name || table.name || `table_${index}`,
        rows: table.row_count || Math.floor(Math.random() * 1000000) + 1000,
        columns: table.column_count || Math.floor(Math.random() * 20) + 5,
        healthScore: table.health_score || Math.floor(Math.random() * 30) + 70,
        validations: table.validation_count || Math.floor(Math.random() * 10),
        lastUpdated: table.last_updated || new Date().toISOString(),
        issues: table.issue_count || Math.floor(Math.random() * 5),
        schema: table.schema_name || 'public',
        database: table.database_name || selectedConnection?.name
      }));
      
      setRealTableData(processedTables);
    }
  }, [tablesQuery.data, selectedConnection]);


  const getHealthScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-50';
    if (score >= 75) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const handleExploreTable = (tableName) => {
    // Show how to navigate to table details
    const connectionSlug = selectedConnection?.slug;
    if (connectionSlug) {
      navigate(`/explorer/${connectionSlug}/tables/${encodeURIComponent(tableName)}`);
    }
  };

  const handleContinue = async () => {
    try {
      // Update progress in backend
      await userAPI.updateOnboardingProgress({
        current_step: 'data',
        steps_completed: ['welcome', 'data'],
        completion_rate: 40, // 2 of 5 steps
        data_explored: {
          connection_id: selectedConnection?.id,
          tables_viewed: realTableData.length,
          completed_at: new Date().toISOString()
        }
      });
      
      console.log('[WelcomeData] Updated onboarding progress');
    } catch (error) {
      console.error('[WelcomeData] Error tracking progress:', error);
      // Continue with navigation even if tracking fails
    }
    
    navigate('/welcome/features');
  };

  const handleBack = () => {
    navigate('/welcome');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Logo size="medium" withText={true} />
            <div className="text-sm text-gray-500">Welcome Tour • Step 2 of 5</div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full" style={{ width: '40%' }}></div>
            </div>
            <span className="ml-3 text-sm text-gray-600">2 of 5</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 rounded-full mb-4">
            <ChartBarIcon className="w-6 h-6 text-white" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Your Data Landscape
          </h1>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Let's explore what data your team is monitoring and how Sparvi helps 
            maintain data quality across your organization.
          </p>
        </div>

        {/* Connection Selector */}
        {connections && connections.length > 1 && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Select a Data Source to Explore</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {connections.map((conn) => (
                <button
                  key={conn.id}
                  onClick={() => setSelectedConnection(conn)}
                  className={`p-4 rounded-lg border text-left transition-colors ${
                    selectedConnection?.id === conn.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <ServerIcon className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <div className="font-medium text-gray-900">{conn.name}</div>
                      <div className="text-sm text-gray-500">{conn.connection_type}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Data Overview */}
        {selectedConnection && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedConnection.name} Overview
              </h2>
              <div className="flex items-center text-sm text-gray-500">
                <ServerIcon className="w-4 h-4 mr-1" />
                {selectedConnection.connection_type}
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <TableCellsIcon className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-blue-900">
                  {tablesQuery.data?.length || realTableData.length}
                </div>
                <div className="text-blue-700 text-sm">Tables Monitored</div>
              </div>

              <div className="bg-green-50 rounded-lg p-4 text-center">
                <CheckCircleIcon className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-green-900">
                  {validationsSummary.data?.total_validations || realTableData.reduce((sum, table) => sum + table.validations, 0)}
                </div>
                <div className="text-green-700 text-sm">Active Validations</div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <ChartBarIcon className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-purple-900">
                  {validationsSummary.data?.average_health_score || 
                   Math.round(realTableData.reduce((sum, table) => sum + table.healthScore, 0) / realTableData.length || 0)}
                </div>
                <div className="text-purple-700 text-sm">Avg Health Score</div>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-yellow-900">
                  {validationsSummary.data?.total_issues || realTableData.reduce((sum, table) => sum + table.issues, 0)}
                </div>
                <div className="text-yellow-700 text-sm">Open Issues</div>
              </div>
            </div>

            {/* Table List */}
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Tables</h3>
            <div className="space-y-3">
              {tablesQuery.isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading tables...</p>
                </div>
              ) : realTableData.length > 0 ? (
                realTableData.map((table, index) => (
                  <div 
                    key={table.name}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center flex-1">
                      <TableCellsIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{table.name}</div>
                        <div className="text-sm text-gray-500">
                          {table.rows.toLocaleString()} rows • {table.columns} columns • 
                          {table.validations} validations active
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      {/* Health Score */}
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${getHealthScoreColor(table.healthScore)}`}>
                        {table.healthScore}% healthy
                      </div>

                      {/* Issues Badge */}
                      {table.issues > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {table.issues} issue{table.issues !== 1 ? 's' : ''}
                        </span>
                      )}

                      {/* Explore Button */}
                      <button
                        onClick={() => handleExploreTable(table.name)}
                        className="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        <EyeIcon className="w-4 h-4 mr-1" />
                        Explore
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <TableCellsIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No tables found for this connection</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Key Insights */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">What This Means</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start">
                <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-gray-900">Health Scores</h3>
                  <p className="text-sm text-gray-600">Show overall data quality for each table based on validation results.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-gray-900">Active Validations</h3>
                  <p className="text-sm text-gray-600">Automated checks running continuously to catch data quality issues.</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start">
                <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-gray-900">Data Explorer</h3>
                  <p className="text-sm text-gray-600">Click "Explore" on any table to dive deeper into the data structure and quality.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-gray-900">Issue Tracking</h3>
                  <p className="text-sm text-gray-600">Open issues help prioritize what data quality problems need attention.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8">
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back
          </button>
          
          <button
            onClick={handleContinue}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Continue to Features
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeDataPage;